<script setup lang="ts">
import { computed } from 'vue'

import { Button, useBreakpoints, type ButtonSize } from '@collector/shared-ui'
import {
  Participant,
  SoccerIncidentAcronymMap,
  SoccerIncidentId,
  SoccerStatId,
} from '@collector/sportsapi-client-legacy'
import {
  useBlockades,
  useEventIncidentsQueue,
} from '@desktop/views/Relation/Sports/composables'

interface Props {
  participant: Participant
  size?: ButtonSize
}

const props = defineProps<Props>()

const { isPitchBlocked } = useBlockades()
const eventIncidentsQueue = useEventIncidentsQueue()
const { breakpoints } = useBreakpoints()

const buttonSize = computed(
  () => props.size ?? (breakpoints['2md'].value ? 'md' : 'xs'),
)

const isShortHanded = computed(
  () =>
    props.participant.stats?.some(
      ({ id, value }) => id === SoccerStatId.PlayShortHanded && value === '1',
    ) ?? false,
)

const buttonLabel = computed(() => {
  if (!breakpoints['2md'].value) {
    return isShortHanded.value
      ? SoccerIncidentAcronymMap[SoccerIncidentId.PlayerReturnToPitch]
      : SoccerIncidentAcronymMap[SoccerIncidentId.PlayShortHanded]
  }

  return isShortHanded.value ? 'Player return to pitch' : 'Play short-handed'
})

function toggleShortHanded(): void {
  if (isPitchBlocked()) {
    return
  }

  eventIncidentsQueue.addIncident({
    incident_id: isShortHanded.value
      ? SoccerIncidentId.PlayerReturnToPitch
      : SoccerIncidentId.PlayShortHanded,
    participant_team_id: props.participant.id,
  })
}
</script>

<template>
  <Button
    class="text-neutral-dark-0 border-neutral-light-0 max-w-28 shrink-0 px-2! text-xs leading-tight font-bold whitespace-normal enabled:hover:border-transparent"
    :class="{
      'bg-error-5 enabled:hover:bg-error-6 text-neutral-light-10':
        isShortHanded,
      'bg-neutral-light-7 enabled:hover:bg-error-6/60': !isShortHanded,
    }"
    :size="buttonSize"
    :disabled="isPitchBlocked()"
    @click="toggleShortHanded"
  >
    {{ buttonLabel }}
  </Button>
</template>
